import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'
import { useHistory, useParams } from 'react-router'
import { TickCircle, TickSquare } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Button from '../../../../atoms/Button2'
import DropdownButton from '../../../../atoms/DropdownButton'
import Tooltip from '../../../../atoms/Tooltip'

import { Routes } from '../../../../../Routes'
import { AddSuccessAlert } from '../../../../../slices/alert'
import { useTaskAPI } from '../../../../../providers/TaskAPIProvider'
import { fetchStatus } from '../../../../../slices/taskStatus'
import { removeFavorite } from '@SonarCX/slices/favorites'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import useStyles from './styles'

const HeaderButtons = ({
  markComplete,
  hideComplete,
  hideGoto,
  disableComplete,
  hasAllSubtasksClosed,
  canClose,
  alreadyComplete,
  saveButton,
  taskID,
  title,
  canEdit,
  variant,
  isClosed,
  isFavorite = false,
  onClose = () => {},
  ...props
}) => {
  const { id } = useParams()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const taskStatus = useAppSelector(storeState => storeState.taskStatus)
  const { statuses, loaded: statusesLoaded } = taskStatus
  const { markCloseTask } = useTaskAPI()
  const { user } = useEnvironment()

  const [completeStatuses, setCompleteStatuses] = React.useState([])
  const [disable, setDisable] = React.useState(false)

  const classes = useStyles()

  const CloseIcon = props => <TickSquare size={theme.size.base} {...props} />

  React.useEffect(() => {
    if (!taskStatus.loaded && !taskStatus.loading) {
      dispatch(fetchStatus())
    }
  }, [taskStatus, dispatch])

  React.useEffect(() => {
    if (statusesLoaded) {
      setCompleteStatuses(
        statuses.filter(status => status.base_status === 'completed')
      )
    }
  }, [statusesLoaded, statuses])

  React.useEffect(() => {
    if (!taskStatus.loaded && !taskStatus.loading) {
      dispatch(fetchStatus())
    }
  }, [taskStatus, dispatch])

  const handleCloseClick = React.useCallback(async () => {
    try {
      setDisable(true)
      await markCloseTask(taskID ?? id, id ? false : true)
      if (isFavorite) {
        await dispatch(removeFavorite(user?.id, taskID ?? id))
      }
      dispatch(AddSuccessAlert('Changes saved'))
      setDisable(false)
      onClose()
    } catch (err) {
      console.error(`Error closing task ${err}`)
      dispatch(AddSuccessAlert('Error closing task'))
    }
  }, [dispatch, id, isFavorite, markCloseTask, taskID, user, onClose])

  const handleGotoTask = React.useCallback(() => {
    history.push(`${Routes.TASKS}/${taskID}`)
  }, [history, taskID])

  const handleOptionSelected = React.useCallback(
    status => {
      markComplete(status)
    },
    [markComplete]
  )

  if (hideComplete && hideGoto) {
    return null
  }

  return (
    <div className={variant ? classes[variant] : classes.root} {...props}>
      {!hideGoto && (
        <div
          onClick={handleGotoTask}
          className={clsx(classes.font, classes.goto)}
        >
          GO TO TASK
        </div>
      )}

      {!hideComplete && canEdit && (
        <Tooltip
          title={
            disableComplete && !alreadyComplete
              ? title ?? 'Cannot complete task until subtasks are all completed'
              : ''
          }
        >
          <div>
            <DropdownButton
              options={completeStatuses}
              renderOption={opt => (
                <div className={classes.name}>
                  <span>Change to status</span> {opt.name}
                </div>
              )}
              onSelect={handleOptionSelected}
              disabled={disableComplete}
              autoWidth
            >
              <Button
                disabled={disableComplete}
                className={clsx(classes.font, classes.button)}
                startIcon={<TickCircle size={theme.size.sm} className='icon' />}
              >
                MARK COMPLETE
              </Button>
            </DropdownButton>
          </div>
        </Tooltip>
      )}

      {disableComplete && alreadyComplete && canClose && (
        <Tooltip
          title={
            !hasAllSubtasksClosed
              ? 'Cannot close task until subtasks are all closed'
              : ''
          }
        >
          <div>
            <Button
              onClick={handleCloseClick}
              disabled={disable || !hasAllSubtasksClosed || isClosed}
              loading={disable}
              loadingPosition='start'
              className={clsx(classes.font, classes.button)}
              startIcon={<CloseIcon size={theme.size.sm} className='icon' />}
            >
              CLOSE TASK
            </Button>
          </div>
        </Tooltip>
      )}

      {saveButton &&
        saveButton(
          clsx(
            classes.font,
            classes.button,
            !hideComplete && classes.endButton
          ),
          'icon'
        )}
    </div>
  )
}

export default HeaderButtons
